import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useApolloClient, ApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebounce } from '@uidotdev/usehooks';
import {
  normalizeFilter,
  filterKey as keyOf,
  moveSelection,
  deletePlan,
  markStep,
  nextSteps,
  type DeleteStep,
  type FilterSelection,
  type ImageFilter,
  type ImageSelection,
  type ImageSort,
} from '@arthelper/shared/image-library';
import { atStep, failureProperties, stepOf } from '@arthelper/shared/upload-failure';
import { putPresignedFile, type UploadContext } from '@arthelper/ui';

import { useAuth } from './auth';
import { useAnalytics } from './analytics';
import { processFile } from '../utils/processFile';
import { removeFailedUpload, saveFailedUpload } from '../utils/failed-uploads';
import { useFailedUploads, type FailedUpload } from './failed-uploads';
import {
  ensureLeadingSlash,
  createCollectionMappings,
  buildCollectionTree,
  CollectionMappings,
  CollectionTree,
} from '../utils/collection';
import {
  CREATE_IMAGE_MUTATION,
  UPDATE_IMAGE_MUTATION,
  REMOVE_IMAGES_MUTATION,
  MOVE_IMAGES_MUTATION,
  CREATE_COLLECTION,
  UPDATE_COLLECTION,
  DELETE_COLLECTION,
  GENERATE_SIGNED_URL,
  CREATE_SERIES,
  UPDATE_SERIES,
  DELETE_SERIES,
} from '../graphql/mutations';
import {
  LIBRARY_IMAGES_QUERY,
  LIBRARY_COLLECTIONS_QUERY,
  LIBRARY_STATS_QUERY,
  LIBRARY_VISIBILITY_STATS_QUERY,
} from '../graphql/queries';
import { DisplayOptions, Tag, ImageItem, UploadingImage, Collection, Series } from '~/types';
import { ROUTES_CONFIG } from '../routes-config';
import { SelectionStateProvider } from '~/components/DragSelect';
import { trackUploadActivation } from '@arthelper/shared/facebook-pixel';
import { uploadOrigin, type UploadSource } from '@arthelper/shared/upload-origin';

const PAGE_SIZE = 60;
const SEARCH_DELAY_MS = 300;
const POLL_MS = 5000;
/** `ImageFilter.ids` takes at most 100; more in flight rotate through the window. */
const POLL_LIMIT = 100;
const IN_FLIGHT = ['uploaded', 'processing'];
const SETTLED_FAILED = ['failed', 'error'];
/** Mirrors the grid's `img.status !== 'processed'` skip: totals and pages cover processed rows only. */
const PROCESSED: string[] = ['processed'];

const SORT_FIELDS = { createdAt: 'CREATED_AT', title: 'TITLE', fileName: 'FILE_NAME' } as const;
const VISIBILITY = { all: 'ALL', public: 'PUBLIC', private: 'PRIVATE' } as const;

type SortCriteria = keyof typeof SORT_FIELDS;
type VisibilityFilter = keyof typeof VISIBILITY;

/**
 * Which slice of the library the grid connection reads. `undefined` - no my-artwork
 * screen is mounted, so the whole library (newest first) backs `images` for the other
 * consumers; `null` - the my-artwork root (unfiled images); a string - a folder path.
 */
type LibraryScope = string | null | undefined;

export interface LibraryStats {
  count: number;
  totalBytes: number | null;
}

export interface VisibilityCounts {
  all: number;
  public: number;
  private: number;
  /** Processed images across the whole library, for the tree's root row. */
  library: number;
}

export interface KnownTag extends Tag {
  count: number;
}

interface RefetchResult {
  data: { collections: Collection[] };
}

interface ImageContextType {
  /** Upload placeholders, then just-processed uploads, then the loaded pages of the grid connection. */
  images: ImageItem[];
  /** Every row loaded so far (grid pages + tracked uploads, any status). Not the whole library. */
  allImages: ImageItem[];
  collections: Collection[];
  loading: boolean;
  collectionTree: CollectionTree;
  rootImages: ImageItem[];
  collectionMappings: CollectionMappings;
  /** Loads the next grid page; resolves once it lands. */
  loadMore: () => Promise<unknown>;
  hasMore: boolean;
  /** Processed rows matching the active folder + filters (server count, not loaded rows). */
  totalCount: number;
  /** Unfiltered whole-library count (every status) and storage, for the header. */
  libraryStats: LibraryStats | null;
  /** VisibilityFilterTabs counts over the active folder scope. */
  visibilityCounts: VisibilityCounts | null;
  /** Tags seen on any loaded row this session, with the number of loaded rows carrying each. */
  knownTags: KnownTag[];
  setLibraryScope: (scope: LibraryScope) => void;
  /** The grid's current filter (null while its folder is unresolved) and sort. */
  activeFilter: ImageFilter | null;
  activeSort: ImageSort;
  /** `filterKey(activeFilter, activeSort)`; a select-all pinned to another key is stale. */
  activeFilterKey: string;
  /** The current first page's server `asOf`. */
  asOf: string | null;
  selectAllState: FilterSelection | null;
  setSelectAllState: (selection: FilterSelection | null) => void;
  /**
   * Runs a shared delete plan's remaining steps in order, stopping at the first failure.
   * `removed` is the server's count of images this run deleted.
   */
  runDeletePlan: (plan: DeleteStep[]) => Promise<{ plan: DeleteStep[]; removed: number }>;
  uploadImage: ({
    file,
    collectionId,
    source,
    retryOf,
    uploadContext,
  }: {
    file: File;
    collectionId: string | null;
    /** The surface running the upload — stored on the image and sent to Amplitude. */
    source: UploadSource;
    /** The failed upload's id: a retry keeps it, so its stored record goes only once this one lands. */
    retryOf?: string;
    uploadContext?: UploadContext;
  }) => Promise<ImageItem>;
  /** Uploads that never became artwork, this session's and earlier ones (#4159). */
  failedUploads: FailedUpload[];
  retryUpload: (item: FailedUpload) => Promise<void>;
  dismissFailedUpload: (item: FailedUpload) => Promise<void>;
  /** True once the member removed this failed upload (vs. retried it). */
  wasDismissed: (id: string) => boolean;
  updateImage: (
    id: string,
    changes: Partial<Omit<ImageItem, 'tags'> & { tags?: Tag[] | string[] }>
  ) => Promise<ImageItem>;
  deleteImage: (id: string | string[]) => Promise<void>;
  toggleVisibility: (image: ImageItem) => Promise<void>;
  createCollection: (collection: Partial<Collection>) => Promise<Collection>;
  updateCollection: (id: string, changes: Partial<Collection>) => Promise<Collection>;
  deleteCollection: (id: string) => Promise<void>;
  moveToCollection: (itemIds: string[], target: (string | null) | Collection) => Promise<void>;
  createCollectionAndMoveItems: (
    collectionData: Partial<Collection>,
    itemIds: string[]
  ) => Promise<{ collectionId: string }>;
  sortCriteria: SortCriteria;
  setSortCriteria: (criteria: SortCriteria) => void;
  sortDirection: 'asc' | 'desc';
  setSortDirection: (direction: 'asc' | 'desc') => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  visibilityFilter: VisibilityFilter;
  setVisibilityFilter: (filter: VisibilityFilter) => void;
  displayOptions: DisplayOptions;
  setDisplayOptions: (options: Partial<DisplayOptions>) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  tagFilterMode: 'AND' | 'OR';
  setTagFilterMode: (mode: 'AND' | 'OR') => void;
  editingImageId: string | null;
  setEditingImageId: (id: string | null) => void;
  uploadingImages: Record<string, UploadingImage>;
  createSeries: (imageIds: string[], collectionIds: string[], name: string) => Promise<Series>;
  updateSeries: (id: string, changes: Partial<Collection>) => Promise<Collection>;
  deleteSeries: (id: string) => Promise<void>;
  /**
   * Re-reads the folders (names, paths, counts) - never the image rows; an image's full row
   * is read by id (`ARTWORK_EDIT_IMAGE_QUERY`). Membership changes use `refreshLibrary`.
   */
  refetchData: () => Promise<RefetchResult | null>;
  /** After a membership change: the grid's first page, the stats and the folder counts. */
  refreshLibrary: () => Promise<unknown>;
}

const ImageContext = createContext<ImageContextType | null>(null);

function scopeFilter(scope: LibraryScope, pathToId: Record<string, string>): ImageFilter | null {
  if (scope === undefined) {
    return {};
  }

  if (scope === null) {
    return { unfiled: true };
  }

  const collectionId = pathToId[ensureLeadingSlash(scope)];
  if (!collectionId) {
    return null;
  }

  return { collectionId };
}

/**
 * The scope my-artwork's ImageManager is about to set, read from the URL up front: the
 * provider sits above the router and the page is lazy, so starting at `undefined` there
 * would first fetch a whole-library page nobody shows.
 */
function initialScope(): LibraryScope {
  if (window.location.pathname !== ROUTES_CONFIG['/my-artwork'].path) {
    return undefined;
  }

  return new URLSearchParams(window.location.search).get('collection') || null;
}

function inScope(image: ImageItem, scope: LibraryScope, pathToId: Record<string, string>) {
  if (scope === undefined) {
    return true;
  }

  if (scope === null) {
    return !image.collectionId;
  }

  return image.collectionId === pathToId[ensureLeadingSlash(scope)];
}

/** The folder a deleted folder's images go to: its parent, or the root (null). */
export function parentFolderId(path: string, pathToId: Record<string, string>): string | null {
  const parts = ensureLeadingSlash(path).split('/').filter(Boolean);
  if (parts.length < 2) {
    return null;
  }

  return pathToId['/' + parts.slice(0, -1).join('/')] ?? null;
}

/** Artwork ids are numeric; folder keys are paths and upload placeholders are `client-…`. */
export function isArtworkId(key: string) {
  return !key.includes('/') && !isNaN(Number(key));
}

/** Uploaded after the select-all's server `asOf`: the server skips it, so the grid must too. */
export function createdAfter(createdAt: string | undefined, asOf: string | undefined) {
  if (!createdAt || !asOf) {
    return false;
  }

  return Date.parse(createdAt) > Date.parse(asOf);
}

/** A loaded image the select-all's filter snapshot covers: a processed artwork no newer than asOf. */
export function inSnapshot(image: ImageItem, asOf: string | undefined) {
  return image.status === 'processed' && isArtworkId(String(image.id)) && !createdAfter(image.createdAt, asOf);
}

/** The loaded images a select-all's snapshot covers; none without one. */
function snapshotIds(images: ImageItem[], selectAll: FilterSelection | null) {
  if (!selectAll) {
    return [];
  }

  return images.filter((image) => inSnapshot(image, selectAll.asOf)).map((image) => String(image.id));
}

/** A BAD_USER_INPUT (e.g. over the 10,000 cap) is shown verbatim; anything else gets `fallback`. */
export function selectionErrorMessage(error: unknown, fallback: string) {
  const graphQLErrors = (error as any)?.graphQLErrors ?? [];
  const userError = graphQLErrors.find((item: any) => item?.extensions?.code === 'BAD_USER_INPUT');
  return userError?.message || fallback;
}

/** `deleteCollection` rejected because the folder no longer exists. */
function isCollectionGone(error: unknown) {
  const graphQLErrors = (error as any)?.graphQLErrors ?? [];
  return graphQLErrors.some((item: any) => item?.message === 'Collection not found');
}

function createdAtOf(image: ImageItem) {
  return image.createdAt ? new Date(image.createdAt).getTime() : 0;
}

function newestFirst(a: ImageItem, b: ImageItem) {
  return createdAtOf(b) - createdAtOf(a);
}

/** The ids to poll this tick: all of them, or a rotating window of the newest 100. */
function pollWindow(ids: string[], tick: number) {
  if (ids.length <= POLL_LIMIT) {
    return ids;
  }

  const start = (tick * POLL_LIMIT) % ids.length;
  return [...ids, ...ids].slice(start, start + POLL_LIMIT);
}

/** Polled rows replace their tracked copies; a polled id the server no longer returns stops being tracked. */
function applyPoll(prev: Record<string, ImageItem>, ids: string[], rows: ImageItem[]) {
  const byId = new Map(rows.map((row) => [String(row.id), row]));

  return ids.reduce(
    (memo, id) => {
      const row = byId.get(id);
      if (row) {
        memo[id] = row;
        return memo;
      }

      delete memo[id];
      return memo;
    },
    { ...prev }
  );
}

function edgesOf(data: any): ImageItem[] {
  return (data?.myImages?.edges ?? []).map((edge: any) => edge.node);
}

/** Merges each loaded row's tags into the index; returns `prev` untouched when nothing changed. */
function indexTags(prev: Record<string, Tag[]>, rows: ImageItem[]) {
  const changed = rows.filter((row) => row.tags && JSON.stringify(prev[row.id]) !== JSON.stringify(row.tags));
  if (changed.length === 0) {
    return prev;
  }

  return changed.reduce(
    (memo, row) => {
      memo[row.id] = row.tags as Tag[];
      return memo;
    },
    { ...prev }
  );
}

function countTags(index: Record<string, Tag[]>): KnownTag[] {
  const counts = Object.values(index)
    .flat()
    .reduce((memo, tag) => {
      const existing = memo.get(tag.id);
      memo.set(tag.id, { ...tag, count: (existing?.count ?? 0) + 1 });
      return memo;
    }, new Map<string, KnownTag>());

  return [...counts.values()].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

/** Adds the seeded rows not tracked yet; tracked copies are newer than a seed read. */
function mergeSeed(prev: Record<string, ImageItem>, rows: ImageItem[]) {
  return rows.reduce((memo, row) => ({ ...memo, [row.id]: memo[row.id] ?? row }), prev);
}

/**
 * Upload processing state. `images.status` (uploaded -> processing -> processed |
 * failed | error) is set by the image processor, never by the client, so the only way
 * to see it move is to read it: `myImages(filter: { ids })` for the in-flight ids every
 * 5 s, and only while one of them is still uploaded/processing. Seeded on mount from the
 * newest 100 in-flight rows so a reload keeps polling; while the server reports more, each
 * tick that finds fewer than 100 tracked re-seeds, so older in-flight uploads are picked up
 * as newer ones finish.
 */
function useProcessingPoll({
  enabled,
  apolloClient,
  onSeed,
}: {
  enabled: boolean;
  apolloClient: ApolloClient<object>;
  onSeed: (ids: string[]) => void;
}) {
  const [tracked, setTracked] = useState<Record<string, ImageItem>>({});
  // More in flight on the server than the last seed returned.
  const [hasMore, setHasMore] = useState(false);
  const trackedRef = useRef(tracked);
  trackedRef.current = tracked;

  const seed = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({
        query: LIBRARY_IMAGES_QUERY,
        variables: { first: POLL_LIMIT, filter: normalizeFilter({ statuses: IN_FLIGHT }) },
        fetchPolicy: 'network-only',
      });
      const fresh = edgesOf(data).filter((row) => !trackedRef.current[row.id]);
      setHasMore(!!data?.myImages?.pageInfo?.hasNextPage);
      if (fresh.length === 0) {
        return;
      }

      setTracked((prev) => mergeSeed(prev, fresh));
      onSeed(fresh.map((row) => row.id));
    } catch (error) {
      // Reads are idempotent: the next tick simply asks again.
      console.error('[ImageContext] Processing seed failed:', error);
    }
  }, [apolloClient, onSeed]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    seed();
  }, [enabled, seed]);

  const inFlightIds = useMemo(() => {
    return Object.values(tracked)
      .filter((row) => IN_FLIGHT.includes(row.status || ''))
      .sort(newestFirst)
      .map((row) => String(row.id));
  }, [tracked]);

  const inFlightRef = useRef(inFlightIds);
  inFlightRef.current = inFlightIds;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const polling = enabled && (inFlightIds.length > 0 || hasMore);

  useEffect(() => {
    if (!polling) {
      return;
    }

    let tick = 0;
    const intervalId = setInterval(async () => {
      if (hasMoreRef.current && inFlightRef.current.length < POLL_LIMIT) {
        await seed();
      }

      const ids = pollWindow(inFlightRef.current, tick);
      tick += 1;
      if (ids.length === 0) {
        return;
      }

      try {
        const { data } = await apolloClient.query({
          query: LIBRARY_IMAGES_QUERY,
          variables: { first: ids.length, filter: normalizeFilter({ ids }) },
          fetchPolicy: 'network-only',
        });
        setTracked((prev) => applyPoll(prev, ids, edgesOf(data)));
      } catch (error) {
        // Reads are idempotent: the next tick simply asks again.
        console.error('[ImageContext] Processing poll failed:', error);
      }
    }, POLL_MS);

    return () => clearInterval(intervalId);
  }, [polling, apolloClient, seed]);

  const track = useCallback((row: ImageItem) => {
    setTracked((prev) => ({ ...prev, [row.id]: row }));
  }, []);

  return { tracked, track };
}

export function ImageProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const apolloClient = useApolloClient();
  const { amplitude, intercom } = useAnalytics();
  const { t } = useTranslation('artwork');

  // State for sorting
  const [sortCriteria, setSortCriteria] = useState<SortCriteria>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // State for search
  const [searchQuery, setSearchQuery] = useState('');
  // State for tag filtering
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagFilterMode, setTagFilterMode] = useState<'AND' | 'OR'>('AND');
  // Visibility filter (All / On profile / Private). Lives in context so it persists
  // across grid/list view switches and both views apply the same value.
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [libraryScope, setLibraryScope] = useState<LibraryScope>(initialScope);
  // Select-all: the filter snapshot + server asOf it was pinned to, minus unchecked tiles.
  const [selectAllState, setSelectAllState] = useState<FilterSelection | null>(null);

  // State for recent uploads
  const [recentUploadBatchIds, setRecentUploadBatchIds] = useState<Set<string>>(new Set());

  // Helper function to clear recent batch
  const clearRecentBatch = useCallback(() => {
    // Check if the set is actually non-empty before creating a new one
    if (recentUploadBatchIds.size > 0) {
      setRecentUploadBatchIds(new Set());
    }
  }, [recentUploadBatchIds.size]); // Depend on size to avoid unnecessary calls

  const addRecent = useCallback((ids: string[]) => {
    setRecentUploadBatchIds((prev) => new Set([...prev, ...ids]));
  }, []);

  // State for display options
  const [displayOptions, setDisplayOptionsState] = useState<DisplayOptions>(() => {
    // Try to get from localStorage
    const savedOptions = localStorage.getItem('imageDisplayOptions');
    if (savedOptions) {
      try {
        return JSON.parse(savedOptions);
      } catch (e) {
        console.error('Failed to parse saved display options', e);
      }
    }
    // Default values
    return {
      showTitle: false,
      showFilename: false,
      showDate: false,
      showTags: false,
    };
  });

  // Function to update display options
  const setDisplayOptions = useCallback((options: Partial<DisplayOptions>) => {
    setDisplayOptionsState((prev) => {
      const newOptions = { ...prev, ...options };
      // Save to localStorage
      localStorage.setItem('imageDisplayOptions', JSON.stringify(newOptions));
      return newOptions;
    });
  }, []);

  // State to track uploading images
  const [uploadingImages, setUploadingImages] = useState<Record<string, UploadingImage>>({});

  // Folders: slim (six previews + a server count each), never their image lists.
  const {
    data: collectionsData,
    loading: collectionsLoading,
    refetch: refetchCollections,
  } = useQuery(LIBRARY_COLLECTIONS_QUERY, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const collections: Collection[] = useMemo(() => collectionsData?.collections || [], [collectionsData]);
  const collectionMappings = useMemo(() => createCollectionMappings(collections), [collections]);
  const collectionTree = useMemo(
    () => buildCollectionTree(collections, collectionMappings.pathToId),
    [collections, collectionMappings]
  );

  // The grid connection: folder scope + debounced search + tags + visibility + sort, all
  // answered by the server. A change to any of them re-queries from the first page.
  const debouncedSearch = useDebounce(searchQuery.trim(), SEARCH_DELAY_MS);
  const scope = useMemo(
    () => scopeFilter(libraryScope, collectionMappings.pathToId),
    [libraryScope, collectionMappings]
  );
  const filter = useMemo(() => {
    if (!scope) {
      return null;
    }

    return normalizeFilter({
      ...scope,
      query: debouncedSearch,
      tagIds: selectedTags,
      tagMatch: tagFilterMode === 'AND' ? 'ALL' : 'ANY',
      visibility: VISIBILITY[visibilityFilter],
      statuses: PROCESSED,
    });
  }, [scope, debouncedSearch, selectedTags, tagFilterMode, visibilityFilter]);
  const sort: ImageSort = useMemo(
    () => ({ field: SORT_FIELDS[sortCriteria], direction: sortDirection === 'asc' ? 'ASC' : 'DESC' }),
    [sortCriteria, sortDirection]
  );

  const {
    data: pageData,
    previousData: previousPageData,
    loading: pageLoading,
    fetchMore,
    refetch: refetchPage,
  } = useQuery(LIBRARY_IMAGES_QUERY, {
    variables: { first: PAGE_SIZE, filter, sort },
    skip: !user || !filter,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  // Keep the last filter's rows on screen while the next filter's first page loads.
  const connection = (pageData ?? previousPageData)?.myImages;
  const connectionRows: ImageItem[] = useMemo(
    () => edgesOf(pageData ?? previousPageData),
    [pageData, previousPageData]
  );
  const pageInfo = pageData?.myImages?.pageInfo;
  // Server time of the current first page; what a select-all pins to, never the client clock.
  const asOf: string | null = pageData?.myImages?.asOf ?? null;
  const activeFilterKey = useMemo(() => keyOf(filter, sort), [filter, sort]);

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage) {
      return Promise.resolve();
    }

    return fetchMore({ variables: { after: pageInfo.endCursor } });
  }, [pageInfo, fetchMore]);

  // Header pill: unfiltered, every status - deliberately not the grid's processed-only totalCount.
  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStats,
  } = useQuery(LIBRARY_STATS_QUERY, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const libraryStats: LibraryStats | null = statsData?.imageStats ?? null;

  // Tab counts over the folder scope (only while a my-artwork screen sets one).
  const visibilityVariables = useMemo(() => {
    if (!scope || libraryScope === undefined) {
      return null;
    }

    return {
      all: normalizeFilter({ ...scope, statuses: PROCESSED }),
      onProfile: normalizeFilter({ ...scope, statuses: PROCESSED, visibility: 'PUBLIC' }),
      private: normalizeFilter({ ...scope, statuses: PROCESSED, visibility: 'PRIVATE' }),
      library: normalizeFilter({ statuses: PROCESSED }),
    };
  }, [scope, libraryScope]);
  const {
    data: visibilityData,
    previousData: previousVisibilityData,
    refetch: refetchVisibility,
  } = useQuery(LIBRARY_VISIBILITY_STATS_QUERY, {
    variables: visibilityVariables ?? undefined,
    skip: !user || !visibilityVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const visibilityCounts: VisibilityCounts | null = useMemo(() => {
    const counts = visibilityData ?? previousVisibilityData;
    if (!counts) {
      return null;
    }

    return {
      all: counts.all.count,
      public: counts.onProfile.count,
      private: counts.private.count,
      library: counts.library.count,
    };
  }, [visibilityData, previousVisibilityData]);

  const { tracked, track } = useProcessingPoll({ enabled: !!user, apolloClient, onSeed: addRecent });

  /**
   * After a membership change (upload processed, delete, move, visibility): the active
   * connection's first page, the stats and the folder counts. Never the whole library.
   */
  const refresh = useCallback(async () => {
    const [, collectionsResult] = await Promise.all([
      filter ? refetchPage() : Promise.resolve(null),
      refetchCollections(),
      refetchStats(),
      visibilityVariables ? refetchVisibility() : Promise.resolve(null),
    ]);
    return collectionsResult;
  }, [filter, visibilityVariables, refetchPage, refetchCollections, refetchStats, refetchVisibility]);

  // Opening a details sheet calls this; re-reading the first page there would drop every
  // page the artist had scrolled through, so it re-reads the folders only.
  const refetchData = useCallback(async (): Promise<RefetchResult | null> => {
    try {
      const result = await refetchCollections();
      return { data: { collections: result?.data?.collections ?? collections } };
    } catch (error) {
      console.error('[ImageContext] Error during refetch:', error);
      return null;
    }
  }, [refetchCollections, collections]);

  // Define mutations
  const [createImageMutation] = useMutation(CREATE_IMAGE_MUTATION);
  const [updateImageMutation] = useMutation(UPDATE_IMAGE_MUTATION);
  const [removeImagesMutation] = useMutation(REMOVE_IMAGES_MUTATION);
  const [moveImagesMutation] = useMutation(MOVE_IMAGES_MUTATION);
  const [createCollectionMutation] = useMutation(CREATE_COLLECTION);
  const [updateCollectionMutation] = useMutation(UPDATE_COLLECTION);
  const [deleteCollectionMutation] = useMutation(DELETE_COLLECTION);
  const [createSeriesMutation] = useMutation(CREATE_SERIES);
  const [updateSeriesMutation] = useMutation(UPDATE_SERIES);
  const [deleteSeriesMutation] = useMutation(DELETE_SERIES);

  // Function to generate a client ID
  function generateClientId(): string {
    const timestamp = Date.now();
    const randomHash = Math.random().toString(36).substring(2, 9);
    // Ensure order is based on timestamp primarily, not just length of keys
    return `client-${timestamp}-${randomHash}`;
  }

  const wrappedSetSortCriteria = useCallback(
    (criteria: SortCriteria) => {
      setSortCriteria(criteria);
      clearRecentBatch();
    },
    [clearRecentBatch]
  ); // Dependency on clearRecentBatch

  const wrappedSetSortDirection = useCallback(
    (direction: 'asc' | 'desc') => {
      setSortDirection(direction);
      clearRecentBatch();
    },
    [clearRecentBatch]
  );

  const wrappedSetSearchQuery = useCallback(
    (query: string) => {
      setSearchQuery(query);
      clearRecentBatch();
    },
    [clearRecentBatch]
  );

  const wrappedSetSelectedTags = useCallback(
    (tags: string[]) => {
      setSelectedTags(tags);
      clearRecentBatch();
    },
    [clearRecentBatch]
  );

  const wrappedSetTagFilterMode = useCallback(
    (mode: 'AND' | 'OR') => {
      setTagFilterMode(mode);
      clearRecentBatch();
    },
    [clearRecentBatch]
  );

  // Uploading > just processed (recent batch, newest first) > the server's page order.
  const combinedImages: ImageItem[] = useMemo(() => {
    // A failed placeholder is listed by FailedUploads with its reason and Retry, not as a
    // grid tile that looks like a finished image.
    const placeholders = Object.values(uploadingImages)
      .filter((img) => img.status !== 'error')
      .map((img) => img as unknown as ImageItem)
      .sort((a, b) => parseInt(b.id.split('-')[1], 10) - parseInt(a.id.split('-')[1], 10));
    const placeholderServerIds = new Set(placeholders.map((img) => img.serverId).filter(Boolean));
    const byId = new Map(connectionRows.map((row) => [String(row.id), row]));

    const recent = [...recentUploadBatchIds]
      .map((id) => byId.get(id) ?? tracked[id])
      .filter(
        (img): img is ImageItem =>
          !!img &&
          img.status === 'processed' &&
          !placeholderServerIds.has(img.id) &&
          inScope(img, libraryScope, collectionMappings.pathToId)
      )
      .sort(newestFirst);
    const recentIds = new Set(recent.map((img) => img.id));
    const rest = connectionRows.filter((img) => !recentIds.has(img.id) && !placeholderServerIds.has(img.id));

    return [...placeholders, ...recent, ...rest];
  }, [uploadingImages, connectionRows, recentUploadBatchIds, tracked, libraryScope, collectionMappings]);

  const allImages: ImageItem[] = useMemo(() => {
    const rows = [...connectionRows, ...Object.values(tracked)];
    return [...new Map(rows.map((row) => [String(row.id), row])).values()];
  }, [connectionRows, tracked]);

  // Root images (uses the new combinedImages logic)
  const rootImages = useMemo(() => combinedImages.filter((img) => !img.collectionId), [combinedImages]);

  // The tag menu has no server list of the artist's tags, so it offers every tag seen on
  // a loaded row this session; the index never shrinks when a tag filter narrows the grid.
  const [tagIndex, setTagIndex] = useState<Record<string, Tag[]>>({});
  useEffect(() => {
    setTagIndex((prev) => indexTags(prev, allImages));
  }, [allImages]);
  const knownTags = useMemo(() => countTags(tagIndex), [tagIndex]);

  // One toast however many files fail together; the list on My Artwork carries the detail.
  const announceFailure = useCallback(() => {
    toast.error(t('image-manager.upload-failures.toast'), {
      id: 'upload-failed',
      description: t('image-manager.upload-failures.toast-description'),
    });
  }, [t]);

  const uploadImage = useCallback(
    async ({
      file,
      collectionId,
      source,
      retryOf,
      uploadContext,
    }: {
      file: File;
      collectionId: string | null;
      source: UploadSource;
      retryOf?: string;
      uploadContext?: UploadContext;
    }): Promise<ImageItem> => {
      const isRetry = !!retryOf;
      const clientId = retryOf ?? generateClientId();
      const previewUrl = URL.createObjectURL(file);
      let imageData: any;

      const initialUploadingImage: UploadingImage = {
        id: clientId,
        title: undefined,
        status: 'uploading',
        uploadProgress: 0,
        preview: previewUrl,
        originalWidth: undefined,
        originalHeight: undefined,
        fileMeta: { fileName: file.name },
        collectionId: collectionId === null ? undefined : collectionId,
        file,
        uploadSource: source,
      };
      setUploadingImages((prev) => ({ ...prev, [clientId]: initialUploadingImage }));

      let createdImage: any;
      try {
        imageData = await atStep('pick', () => processFile(file, clientId));
        if (uploadContext?.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
        setUploadingImages((prev) => {
          if (!prev[clientId]) return prev;
          return {
            ...prev,
            [clientId]: {
              ...prev[clientId],
              originalWidth: imageData.originalWidth,
              originalHeight: imageData.originalHeight,
              fileMeta: imageData.fileMeta,
            } as UploadingImage,
          };
        });

        const signedUrl: string = await atStep('signed_url', async () => {
          const { data: urlData } = await apolloClient.mutate({
            mutation: GENERATE_SIGNED_URL,
            variables: { input: { fileName: file.name, fileType: file.type, type: 'private' } },
          });
          return urlData.generateSignedUrl;
        });
        const originalUrl = signedUrl.split('?')[0];

        await atStep('s3_put', () =>
          putPresignedFile(
            file,
            { url: signedUrl, contentType: file.type },
            uploadContext?.signal ?? new AbortController().signal,
            (fraction) => {
              setUploadingImages((prev) => {
                if (!prev[clientId]) return prev;
                return { ...prev, [clientId]: { ...prev[clientId], uploadProgress: fraction * 100 } as UploadingImage };
              });
              uploadContext?.onProgress(fraction);
            }
          )
        );
        if (uploadContext?.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
        uploadContext?.onProcessing();

        createdImage = await atStep('create', async () => {
          const { data } = await createImageMutation({
            variables: {
              input: {
                originalUrl,
                originalWidth: imageData.originalWidth,
                originalHeight: imageData.originalHeight,
                fileMeta: imageData.fileMeta,
                printMeta: imageData.printMeta,
                status: 'uploaded',
                collectionId: collectionId === null ? undefined : collectionId,
                ...uploadOrigin(source),
              },
            },
          });
          return data.createImage;
        });
      } catch (error) {
        if (uploadContext?.signal.aborted) {
          setUploadingImages((prev) => {
            const next = { ...prev };
            delete next[clientId];
            return next;
          });
          URL.revokeObjectURL(previewUrl);
          throw error;
        }
        const step = stepOf(error);
        const message = error instanceof Error ? error.message : 'Unknown upload error';
        console.error('Upload error for', clientId, error);
        amplitude.track('image_upload_failed', {
          ...failureProperties(step, file, error),
          ...(isRetry ? { is_retry: true } : {}),
        });
        // Kept with its File: the failure has to outlive a reload and Retry has to work.
        setUploadingImages((prev) => {
          if (!prev[clientId]) return prev;
          return {
            ...prev,
            [clientId]: {
              ...prev[clientId],
              status: 'error',
              uploadError: message,
              failedStep: step,
            } as UploadingImage,
          };
        });
        if (user?.user_id) {
          saveFailedUpload({
            id: clientId,
            userId: String(user.user_id),
            file,
            fileName: file.name,
            fileType: file.type,
            step,
            message,
            collectionId,
            uploadSource: source,
            failedAt: new Date().toISOString(),
          });
        }
        announceFailure();

        throw Object.assign(error instanceof Error ? error : new Error(String(error)), { uploadClientId: clientId });
      }

      // The row exists from here on: nothing below may report the upload as failed.
      removeFailedUpload(clientId);
      amplitude.track('image_uploaded', {
        image_id: createdImage.id,
        source,
      });

      trackUploadActivation(createdImage);

      // Add the new server ID to the recent batch, and start polling its status
      setRecentUploadBatchIds((prev) => new Set(prev).add(createdImage.id));
      track({
        ...createdImage,
        status: createdImage.status || 'uploaded',
        collectionId: collectionId ?? null,
        createdAt: new Date().toISOString(),
      });

      setUploadingImages((prev) => {
        if (!prev[clientId]) return prev;
        return {
          ...prev,
          [clientId]: {
            ...prev[clientId],
            status: 'processing',
            uploadProgress: 100,
            serverId: createdImage.id,
            file: undefined,
            preview: prev[clientId].preview,
          } as UploadingImage,
        };
      });

      // The header counts every status, so it moves now; the grid waits for `processed`.
      refetchStats();
      return { ...createdImage, preview: previewUrl };
    },
    [apolloClient, createImageMutation, refetchStats, track, amplitude, user, announceFailure]
  );

  // The mutation alone: its response updates the normalized Image row in the cache.
  const writeImage = useCallback(
    async (id: string, changes: Partial<Omit<ImageItem, 'tags'> & { tags?: Tag[] | string[] }>): Promise<ImageItem> => {
      const serverFields = Object.fromEntries(
        Object.entries(changes).filter(
          ([key]) => !['uploadProgress', 'uploadError', 'preview', 'collection', 'clientId'].includes(key)
        )
      );

      // Convert tags to strings if they are Tag objects
      if (serverFields.tags) {
        serverFields.tags = serverFields.tags.map((tag: any) => (typeof tag === 'string' ? tag : tag.name));
      }

      const { data } = await updateImageMutation({ variables: { input: { id, ...serverFields } } });
      return data.updateImage;
    },
    [updateImageMutation]
  );

  /**
   * Field edits (title, tags, ...) show through the normalized cache with no refetch. A
   * move changes which folder/page the row belongs to, so it re-reads the first page and
   * the stats; a visibility flip does too under a visibility filter, and otherwise only
   * moves the tab counts.
   */
  const updateImage = useCallback(
    async (id: string, changes: Partial<Omit<ImageItem, 'tags'> & { tags?: Tag[] | string[] }>): Promise<ImageItem> => {
      // Read before the write: the mutation response overwrites the cached row.
      const before = allImages.find((img) => String(img.id) === String(id));
      const moved = 'collectionId' in changes && before?.collectionId !== changes.collectionId;
      const flipped = 'isPublic' in changes && before?.isPublic !== changes.isPublic;
      const updated = await writeImage(id, changes);
      if (moved || (flipped && visibilityFilter !== 'all')) {
        await refresh();
        return updated;
      }

      if (flipped && visibilityVariables) {
        await refetchVisibility();
      }
      return updated;
    },
    [allImages, writeImage, refresh, visibilityFilter, visibilityVariables, refetchVisibility]
  );

  /**
   * Flip one work between the profile and private, from whichever control the artist
   * reached for.
   *
   * The grid chip and the context menu each carried their own copy of this - the write,
   * the toast, and in one of them the analytics event - so the two drifted, and a toggle
   * from the chip was never counted. The action lives here once: every surface calls it
   * and they cannot disagree again.
   */
  const toggleVisibility = useCallback(
    async (image: ImageItem) => {
      const isPublic = !image.isPublic;
      const title = image.title || t('image-manager.visibility.untitled');

      try {
        await updateImage(image.id, { isPublic });
        amplitude.track('my_artwork_image_visibility_toggled', { image_id: image.id, is_public: isPublic });
        toast.success(
          isPublic
            ? t('image-manager.visibility.now-public', { title })
            : t('image-manager.visibility.now-private', { title })
        );
      } catch {
        toast.error(t('image-manager.visibility.update-failed'));
      }
    },
    [amplitude, t, updateImage]
  );

  const deleteImage = useCallback(
    async (id: string | string[]): Promise<void> => {
      const ids = Array.isArray(id) ? id : [id];
      await removeImagesMutation({ variables: { selection: { ids } } });
      await refresh();
    },
    [removeImagesMutation, refresh]
  );

  // A failed row went back to the processor: show it in flight and poll it like a new upload.
  const onRetried = useCallback(
    (item: FailedUpload) => {
      setUploadingImages((prev) => {
        const existing = prev[item.id];
        const id = existing ? item.id : generateClientId();
        return {
          ...prev,
          [id]: {
            ...(existing ?? { preview: '', fileMeta: { fileName: item.fileName } }),
            id,
            status: 'processing',
            uploadProgress: 100,
            uploadError: undefined,
            failedStep: undefined,
            serverId: item.serverId,
          } as UploadingImage,
        };
      });
      setRecentUploadBatchIds((prev) => new Set(prev).add(String(item.serverId)));
      track({ id: String(item.serverId), status: 'uploaded', createdAt: new Date().toISOString() } as ImageItem);
    },
    [track]
  );

  const { failedUploads, retryUpload, dismissFailedUpload, wasDismissed, refetchFailed } = useFailedUploads({
    userId: user?.user_id ? String(user.user_id) : null,
    uploadingImages,
    setUploadingImages,
    uploadImage,
    deleteImage: deleteImage as (id: string) => Promise<void>,
    onRetried,
  });

  const createCollection = useCallback(
    async (collection: Partial<Collection>): Promise<Collection> => {
      const { name, path, ephemeral = false } = collection;
      const { data } = await createCollectionMutation({ variables: { input: { name, path, ephemeral } } });
      if (name) {
        amplitude.track('new_folder_created', {
          folder_name: name,
        });
        intercom.trackEvent('new_folder_created', {
          folder_name: name,
        });
      }
      await refetchCollections();
      return data.createCollection;
    },
    [createCollectionMutation, refetchCollections, amplitude, intercom]
  );

  const updateCollection = useCallback(
    async (id: string, changes: Partial<Collection>): Promise<Collection> => {
      const { data } = await updateCollectionMutation({ variables: { input: { id, ...changes } } });
      await refetchCollections();
      return data.updateCollection;
    },
    [updateCollectionMutation, refetchCollections]
  );

  /** One delete-plan step as its request; resolves to the images it deleted. */
  const runStep = useCallback(
    async (step: DeleteStep): Promise<number> => {
      if (step.kind === 'removeImages') {
        const { data } = await removeImagesMutation({ variables: { selection: step.selection } });
        return data?.removeImages?.count ?? 0;
      }

      if (step.kind === 'moveImages') {
        await moveImagesMutation({ variables: { selection: step.selection, collectionId: step.collectionId } });
        return 0;
      }

      // A folder already gone (an earlier attempt committed, or an ancestor took it) is done.
      await deleteCollectionMutation({ variables: { input: { id: step.folderId } } }).catch((error) => {
        if (!isCollectionGone(error)) {
          throw error;
        }
      });
      return 0;
    },
    [removeImagesMutation, moveImagesMutation, deleteCollectionMutation]
  );

  /**
   * Runs the plan's remaining steps (`nextSteps`) strictly in order and stops at the first
   * failure, returning the plan with each step marked. Re-running the returned plan is the
   * Retry: a done `removeImages` is never sent again. Refreshes the first page, stats and
   * folders once, whether it finished or not (earlier steps may have committed).
   */
  const runDeletePlan = useCallback(
    async (plan: DeleteStep[]): Promise<{ plan: DeleteStep[]; removed: number }> => {
      const pending = nextSteps(plan);
      let current = plan;
      let removed = 0;
      for (const step of pending) {
        try {
          removed += await runStep(step);
          current = markStep(current, step.id, 'done');
        } catch (error) {
          console.error('[ImageContext] Delete step failed:', step.id, error);
          current = markStep(current, step.id, 'failed');
          break;
        }
      }
      await refresh().catch((error) => console.error('[ImageContext] Refresh after delete failed:', error));
      return { plan: current, removed };
    },
    [runStep, refresh]
  );

  /**
   * Deletes a folder without losing a single image: every image under it (descendants
   * included, loaded or not) moves to the parent folder (root when there is none), and only
   * then is the folder - with its subfolders - deleted.
   */
  const deleteCollection = useCallback(
    async (id: string): Promise<void> => {
      const collectionToDelete = collections.find((c: Collection) => c.id === id);
      if (!collectionToDelete) throw new Error('Collection not found');
      const plan = deletePlan(
        { mode: 'explicit', ids: [], collectionIds: [id] },
        parentFolderId(collectionToDelete.path, collectionMappings.pathToId)
      );
      const { plan: result } = await runDeletePlan(plan);
      if (nextSteps(result).length > 0) {
        throw new Error('Folder deletion failed');
      }
    },
    [collections, collectionMappings, runDeletePlan]
  );

  /**
   * One moveImages request. A failure shows the error toast with Retry (re-sending the same
   * selection, asOf included) and is rethrown marked `handled`, so callers skip their own toast.
   */
  const sendMove = useCallback(
    async (variables: { selection: ImageSelection; collectionId: string | null }): Promise<void> => {
      try {
        await moveImagesMutation({ variables });
      } catch (error) {
        const retry = () => {
          sendMove(variables)
            .then(() => refresh())
            .catch(() => undefined);
        };
        toast.error(selectionErrorMessage(error, t('image-manager.move.failed')), {
          action: { label: t('image-manager.selection.retry'), onClick: retry },
        });
        throw Object.assign(error instanceof Error ? error : new Error(String(error)), { handled: true });
      }
    },
    [moveImagesMutation, refresh, t]
  );

  const moveToCollection = useCallback(
    async (itemIds: string[], target: (string | null) | Collection): Promise<void> => {
      if (!itemIds.length) return;
      if (typeof itemIds[0] !== 'string' || !itemIds[0].length) throw new Error('Invalid item IDs');

      let targetCollectionId: string | null = null;
      let targetCollection: Collection | null = null;
      let normalizedTargetPath: string | null = null;

      if (typeof target === 'string' || target === null) {
        targetCollectionId = target;
        targetCollection = collections.find((c: Collection) => c.id === targetCollectionId) ?? null;
        if (targetCollectionId && !targetCollection)
          throw new Error(`Target collection ID ${targetCollectionId} not found locally.`);
        normalizedTargetPath =
          targetCollectionId === null ? '/' : targetCollection ? ensureLeadingSlash(targetCollection.path) : null;
      } else {
        targetCollection = target;
        targetCollectionId = targetCollection.id;
        normalizedTargetPath = ensureLeadingSlash(targetCollection.path);
      }

      if (normalizedTargetPath === null) throw new Error('Target collection path could not be determined.');

      // Upload placeholders (`client-…`) have no row yet, so a move never sends them.
      const imageIds: string[] = itemIds.filter(isArtworkId);
      const collectionPaths: string[] = itemIds.filter((id) => id.includes('/'));

      // One request for every image. Dragging the selection while select-all is on moves the
      // whole filter selection (loaded or not) plus its extra picks, minus the unchecked exceptions.
      if (imageIds.length > 0) {
        const variables = {
          selection: moveSelection(imageIds, selectAllState, snapshotIds(combinedImages, selectAllState)),
          collectionId: targetCollectionId,
        };
        await sendMove(variables);
      }

      if (collectionPaths.length > 0) {
        for (const path of collectionPaths) {
          const normalizedSourcePath = ensureLeadingSlash(path);
          if (normalizedTargetPath === normalizedSourcePath) throw new Error('Cannot move a collection into itself');
          if (normalizedTargetPath.startsWith(normalizedSourcePath + '/'))
            throw new Error('Cannot move a collection into its descendant');

          const sourceCollection = collections.find(
            (c: Collection) => ensureLeadingSlash(c.path) === normalizedSourcePath
          );
          if (!sourceCollection) throw new Error(`Source collection not found: ${normalizedSourcePath}`);

          const sourceFolderName = normalizedSourcePath.split('/').filter(Boolean).pop() || '';
          const newPath =
            normalizedTargetPath === '/' ? `/${sourceFolderName}` : `${normalizedTargetPath}/${sourceFolderName}`;

          if (normalizedSourcePath !== newPath) {
            await updateCollectionMutation({ variables: { input: { id: sourceCollection.id, path: newPath } } });
            const childCollections = collections.filter(
              (c: Collection) =>
                ensureLeadingSlash(c.path).startsWith(normalizedSourcePath + '/') && c.id !== sourceCollection.id
            );
            if (childCollections.length > 0) {
              await Promise.all(
                childCollections.map(async (childCollection: Collection) => {
                  const relativePath = ensureLeadingSlash(childCollection.path).slice(normalizedSourcePath.length);
                  const newChildPath = newPath + relativePath;
                  await updateCollectionMutation({
                    variables: { input: { id: childCollection.id, path: newChildPath } },
                  });
                })
              );
            }
          }
        }
      }
      await refresh();
    },
    [collections, selectAllState, combinedImages, sendMove, updateCollectionMutation, refresh]
  );

  const createCollectionAndMoveItems = useCallback(
    async (collectionData: Partial<Collection>, itemIds: string[] = []): Promise<{ collectionId: string }> => {
      const { data } = await createCollectionMutation({ variables: { input: collectionData } });

      if (collectionData.name) {
        amplitude.track('new_folder_created', {
          folder_name: collectionData.name,
        });
      }

      const newCollection = data?.createCollection;
      if (!newCollection?.id) throw new Error('Collection creation failed or did not return an ID.');

      if (itemIds.length > 0) {
        await moveToCollection(itemIds, newCollection);
      }
      return { collectionId: newCollection.id };
    },
    [createCollectionMutation, moveToCollection, amplitude]
  );

  const createSeries = useCallback(
    async (imageIds: string[], collectionIds: string[], name: string): Promise<Series> => {
      const { data } = await createSeriesMutation({ variables: { input: { imageIds, collectionIds, name } } });
      return data.createSeries;
    },
    [createSeriesMutation]
  );

  const updateSeries = useCallback(
    async (id: string, changes: Partial<Collection>): Promise<Collection> => {
      const { data } = await updateSeriesMutation({ variables: { input: { id, ...changes } } });
      return data.updateSeries;
    },
    [updateSeriesMutation]
  );

  const deleteSeries = useCallback(
    async (id: string): Promise<void> => {
      await deleteSeriesMutation({ variables: { input: { id } } });
    },
    [deleteSeriesMutation]
  );

  // Placeholders follow their tracked server row: processed -> the placeholder goes and the
  // row shows at the top (recent batch); failed/error -> the placeholder shows today's
  // error state and polling for it stops.
  useEffect(() => {
    const placeholders = Object.values(uploadingImages).filter((img) => img.serverId && tracked[img.serverId]);
    const done = placeholders.filter((img) => tracked[img.serverId!].status === 'processed');
    const failed = placeholders.filter(
      (img) => img.status !== 'error' && SETTLED_FAILED.includes(tracked[img.serverId!].status || '')
    );
    if (done.length === 0 && failed.length === 0) {
      return;
    }

    setUploadingImages((prev) => {
      const next = { ...prev };
      done.forEach((img) => {
        if (next[img.id]?.preview) {
          URL.revokeObjectURL(next[img.id].preview); // Clean up blob URL
        }
        delete next[img.id];
      });
      failed.forEach((img) => {
        next[img.id] = {
          ...next[img.id],
          status: 'error',
          uploadError: t('media-selector.processing-failed'),
          failedStep: 'processing',
        };
      });
      return next;
    });
    failed.forEach((img) => {
      amplitude.track(
        'image_upload_failed',
        failureProperties('processing', { type: img.fileMeta?.fileType, size: img.fileMeta?.fileSize })
      );
    });
    if (failed.length > 0) {
      announceFailure();
      refetchFailed();
    }
  }, [tracked, uploadingImages, t, amplitude, refetchFailed, announceFailure]);

  // A tracked row reaching `processed` joins the processed-only page, totals and folder counts.
  const processedKey = useMemo(() => {
    return Object.values(tracked)
      .filter((row) => row.status === 'processed')
      .map((row) => row.id)
      .sort()
      .join(',');
  }, [tracked]);
  const lastProcessedKey = useRef(processedKey);
  useEffect(() => {
    if (processedKey === lastProcessedKey.current) {
      return;
    }

    lastProcessedKey.current = processedKey;
    refresh().catch((error) => console.error('[ImageContext] Refresh after processing failed:', error));
  }, [processedKey, refresh]);

  const contextValue = useMemo(
    () => ({
      images: combinedImages,
      allImages,
      collections,
      loading: pageLoading && !pageData,
      collectionTree,
      rootImages,
      collectionMappings,
      loadMore,
      hasMore: !!pageInfo?.hasNextPage,
      totalCount: connection?.totalCount ?? 0,
      libraryStats,
      visibilityCounts,
      knownTags,
      setLibraryScope,
      activeFilter: filter,
      activeSort: sort,
      activeFilterKey,
      asOf,
      selectAllState,
      setSelectAllState,
      runDeletePlan,
      uploadImage,
      failedUploads,
      retryUpload,
      dismissFailedUpload,
      wasDismissed,
      updateImage,
      deleteImage,
      toggleVisibility,
      createCollection,
      updateCollection,
      deleteCollection,
      moveToCollection,
      createCollectionAndMoveItems,
      sortCriteria,
      setSortCriteria: wrappedSetSortCriteria,
      sortDirection,
      setSortDirection: wrappedSetSortDirection,
      searchQuery,
      setSearchQuery: wrappedSetSearchQuery,
      visibilityFilter,
      setVisibilityFilter,
      displayOptions,
      setDisplayOptions,
      selectedTags,
      setSelectedTags: wrappedSetSelectedTags,
      tagFilterMode,
      setTagFilterMode: wrappedSetTagFilterMode,
      editingImageId,
      setEditingImageId,
      uploadingImages,
      createSeries,
      updateSeries,
      deleteSeries,
      refetchData,
      refreshLibrary: refresh,
    }),
    [
      combinedImages,
      allImages,
      collections,
      pageLoading,
      pageData,
      collectionTree,
      rootImages,
      collectionMappings,
      loadMore,
      pageInfo,
      connection,
      libraryStats,
      visibilityCounts,
      knownTags,
      filter,
      sort,
      activeFilterKey,
      asOf,
      selectAllState,
      runDeletePlan,
      uploadImage,
      failedUploads,
      retryUpload,
      dismissFailedUpload,
      wasDismissed,
      updateImage,
      deleteImage,
      toggleVisibility,
      createCollection,
      updateCollection,
      deleteCollection,
      moveToCollection,
      createCollectionAndMoveItems,
      sortCriteria,
      sortDirection,
      searchQuery,
      displayOptions,
      selectedTags,
      tagFilterMode,
      editingImageId,
      setEditingImageId,
      setDisplayOptions,
      wrappedSetSortCriteria,
      wrappedSetSortDirection,
      wrappedSetSearchQuery,
      visibilityFilter,
      wrappedSetSelectedTags,
      wrappedSetTagFilterMode,
      uploadingImages,
      createSeries,
      updateSeries,
      deleteSeries,
      refetchData,
      refresh,
    ]
  );

  // Hold the tree back only for the very first load (folders, first page, header stats),
  // as the whole-library query did; later re-queries keep the screen mounted.
  const ready = useRef(false);
  const firstLoad =
    (collectionsLoading && !collectionsData) || (pageLoading && !connection) || (statsLoading && !statsData);
  if (user && !firstLoad) {
    ready.current = true;
  }

  if (user && !ready.current) {
    return <ImageContext.Provider value={contextValue}>{null}</ImageContext.Provider>;
  }

  return <ImageContext.Provider value={contextValue}>{children}</ImageContext.Provider>;
}

export function useImages() {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useImages must be used within an ImageProvider');
  }
  return context;
}

/** The library's checkbox selection: clearing it also ends a select-all. Mount inside `ImageProvider`. */
export function ImageSelectionProvider({ children }: { children: React.ReactNode }) {
  const { setSelectAllState } = useImages();
  const endSelectAll = useCallback(() => {
    setSelectAllState(null);
  }, [setSelectAllState]);

  return <SelectionStateProvider onClear={endSelectAll}>{children}</SelectionStateProvider>;
}
